/**
 * PURPOSE: Cliente de la importación SISDEU → Winscore (FTR-0048..0050).
 * RESPONSIBILITIES: Estado (por evento/deporte y por institución), vista previa e importación de acreditados de una
 *   institución o de todas las participantes del deporte (FTR-0052), vista previa e importación de "Instituciones
 *   SISDEU" (individuales) y categorías importadas.
 *
 *   EL BACKEND DECIDE. La interfaz nunca habilita la importación por compañía ni por id: muestra el botón solo con
 *   `enabled: true` del estado. FAIL CLOSED: ante cualquier error del estado (red, 404 del módulo apagado, 403 de un rol
 *   sin permiso, respuesta rara) se asume TODO apagado y la integración inactiva, que además es lo que mantiene visibles
 *   los botones legacy (comportamiento de siempre).
 *
 *   CONTEXTO SOLO EN LA RUTA: idevent, idsport e idinstitution viajan en el path; el cuerpo solo lleva `scope`. Nunca se
 *   envían ids SISDEU.
 *
 *   ERRORES de las acciones: se relanza el error de axios para que la interfaz lea `code` y `message` del sobre V2
 *   (sisdeuImportErrorMessage).
 * COLLABORATORS: httpService.js, hooks/useSisdeuImportStatus.js, area/InstitucionesArea/SisdeuImportPanel.jsx,
 *   area/CategoriasIndividualArea/CategoriasIndividualArea.jsx.
 * ROUTE: winscore/src/services/sisdeuImportService.js
 */
import httpService from "./httpService.js";

export const SISDEU_IMPORT_BASE = "/api/hayllis/v2/admin/sisdeu-import";
export const SISDEU_IMPORT_SCOPES = Object.freeze({ APPROVED: "APPROVED", ALL: "ALL" });

/** Todo apagado: el valor de siempre que no haya una confirmación explícita del backend. */
export const SISDEU_IMPORT_STATUS_OFF = Object.freeze({
  enabled: false,
  integrationActive: false,
  applyEnabled: false,
  reason: null,
  message: null,
  sportKind: null,
  eventSource: null,
  actions: Object.freeze({
    previewAccreditations: false,
    applyAccreditations: false,
    previewParticipants: false,
    applyParticipants: false,
    categories: false,
    previewAllAccreditations: false,
    applyAllAccreditations: false,
  }),
  institution: null,
});

const id = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const sportPath = ({ idevent, idsport }) => {
  const event = id(idevent);
  const sport = id(idsport);
  if (!event || !sport) throw new Error("idevent e idsport son obligatorios.");
  return `${SISDEU_IMPORT_BASE}/events/${event}/sports/${sport}`;
};

const institutionPath = ({ idevent, idsport, idinstitution }) => {
  const institution = id(idinstitution);
  if (!institution) throw new Error("idinstitution es obligatorio.");
  return `${sportPath({ idevent, idsport })}/institutions/${institution}`;
};

const unwrap = (response) => response?.data?.data ?? null;
const bool = (value) => value === true;

/** Normaliza el estado del backend leyendo solo booleanos explícitos (fail closed). */
export const normalizeSisdeuImportStatus = (data) => {
  if (!data || typeof data !== "object") return { ...SISDEU_IMPORT_STATUS_OFF };
  const actions = data.actions && typeof data.actions === "object" ? data.actions : {};
  return {
    enabled: bool(data.enabled),
    integrationActive: bool(data.integrationActive),
    applyEnabled: bool(data.applyEnabled),
    reason: typeof data.reason === "string" ? data.reason : null,
    message: typeof data.message === "string" ? data.message : null,
    sportKind: data.sportKind === "COLLECTIVE" || data.sportKind === "INDIVIDUAL" ? data.sportKind : null,
    eventSource: typeof data.eventSource === "string" ? data.eventSource : null,
    actions: {
      previewAccreditations: bool(actions.previewAccreditations),
      applyAccreditations: bool(actions.applyAccreditations),
      previewParticipants: bool(actions.previewParticipants),
      applyParticipants: bool(actions.applyParticipants),
      categories: bool(actions.categories),
      previewAllAccreditations: bool(actions.previewAllAccreditations),
      applyAllAccreditations: bool(actions.applyAllAccreditations),
    },
    institution:
      data.institution && typeof data.institution === "object"
        ? { idinstitution: id(data.institution.idinstitution), linked: bool(data.institution.linked) }
        : null,
  };
};

/** Estado del evento/deporte o, con `idinstitution`, de la institución. Nunca lanza. */
export const getSisdeuImportStatus = async ({ idevent, idsport, idinstitution = null }) => {
  try {
    const path = idinstitution ? `${institutionPath({ idevent, idsport, idinstitution })}/status` : `${sportPath({ idevent, idsport })}/status`;
    return normalizeSisdeuImportStatus(unwrap(await httpService.get(path)));
  } catch (error) {
    return { ...SISDEU_IMPORT_STATUS_OFF };
  }
};

const scopeBody = (scope) => ({ scope: scope === SISDEU_IMPORT_SCOPES.ALL ? SISDEU_IMPORT_SCOPES.ALL : SISDEU_IMPORT_SCOPES.APPROVED });

export const previewSisdeuAccreditations = async ({ idevent, idsport, idinstitution, scope }) =>
  unwrap(await httpService.post(`${institutionPath({ idevent, idsport, idinstitution })}/accreditations/preview`, scopeBody(scope)));

export const importSisdeuAccreditations = async ({ idevent, idsport, idinstitution, scope }) =>
  unwrap(await httpService.post(`${institutionPath({ idevent, idsport, idinstitution })}/accreditations`, scopeBody(scope)));

// FTR-0052: acreditados de TODAS las instituciones participantes del deporte. El cuerpo solo lleva `scope`: las
// instituciones las decide el backend (nunca viaja una institución ni un id SISDEU).
export const previewSisdeuAllAccreditations = async ({ idevent, idsport, scope }) =>
  unwrap(await httpService.post(`${sportPath({ idevent, idsport })}/accreditations/preview`, scopeBody(scope)));

export const importSisdeuAllAccreditations = async ({ idevent, idsport, scope }) =>
  unwrap(await httpService.post(`${sportPath({ idevent, idsport })}/accreditations`, scopeBody(scope)));

export const previewSisdeuParticipants = async ({ idevent, idsport, scope }) =>
  unwrap(await httpService.post(`${sportPath({ idevent, idsport })}/participants/preview`, scopeBody(scope)));

export const importSisdeuParticipants = async ({ idevent, idsport, scope }) =>
  unwrap(await httpService.post(`${sportPath({ idevent, idsport })}/participants`, scopeBody(scope)));

export const getSisdeuCategories = async ({ idevent, idsport }) => {
  const data = unwrap(await httpService.get(`${sportPath({ idevent, idsport })}/categories`));
  return Array.isArray(data?.categories) ? data.categories : [];
};

/** Mensaje para la interfaz: el del sobre V2 si lo hay; si no, uno genérico (nunca el detalle técnico). */
export const sisdeuImportErrorMessage = (error) =>
  (typeof error?.response?.data?.message === "string" && error.response.data.message) ||
  "No se pudo completar la importación SISDEU. Inténtalo nuevamente.";
